import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ToastrService } from 'ngx-toastr';
import { AzureSASCredential, TableClient, TableEntityResult } from '@azure/data-tables';
import { VoucherApplication } from 'app/models/vouchers/voucher-application';
import { VoucherService } from './voucher-service';

@Injectable({
  providedIn: 'root',
})
export class OregonWellAzueService {
  constructor(private toastr: ToastrService, private voucherService: VoucherService) {
    // default
  }

  // Azure Table
  getTableClient(voucherType: string): TableClient {
    let tableClient = null;

    if (voucherType == this.voucherService.lubgwmaTestingVoucherType) {
      tableClient = new TableClient(`https://${environment.azure.storageLubgwma.account}.table.core.windows.net`,
        environment.azure.storageLubgwma.tableName,
        new AzureSASCredential(environment.azure.storageLubgwma.sasToken)
      );
    }

    else if (voucherType == this.voucherService.wildfireVoucherType) {
      tableClient = new TableClient(`https://${environment.azure.storage.account}.table.core.windows.net`,
        environment.azure.storage.tableName,
        new AzureSASCredential(environment.azure.storage.sasToken)
      );
    }

    else if (voucherType == this.voucherService.lubgwmatestingSurvey123VoucherType) {
      tableClient = new TableClient(`https://${environment.azure.storageLubgwmaSurvey123.account}.table.core.windows.net`,
        environment.azure.storageLubgwmaSurvey123.tableName,
        new AzureSASCredential(environment.azure.storageLubgwmaSurvey123.sasToken)
      );
    }

    return tableClient;
  }

  // Count
  async getVoucherImportCount(voucherType: string) {
    try {
      let counter = 0;
      let entitiesIterator = this.getTableClient(voucherType).listEntities();

      for await (const entity of entitiesIterator) {
        counter++;
      }

      return counter;
    }
    catch (e) {
      console.log(e);
      this.toastr.error(e);
      this.toastr.error("Unable to load New Voucher Imports");
      return 0;
    }
  }

  // Import Applications
  async getVoucherImportApplications(voucherType: string): Promise<VoucherApplication[]> {
    let applications: VoucherApplication[] = [];

    try {
      let entitiesIterator = this.getTableClient(voucherType).listEntities();

      for await (const entity of entitiesIterator) {
        if (voucherType == this.voucherService.lubgwmaTestingVoucherType || voucherType == this.voucherService.lubgwmatestingSurvey123VoucherType) {
          applications.push(this.getLubgwmaVoucherApplication(entity, voucherType));
        }
      }
    }
    catch (e) {
      console.log(e);
      this.toastr.error(e);
      this.toastr.error("Unable to load New Voucher Imports");
    }

    return applications;
  }


  // Import LUBGWMA App
  private getLubgwmaVoucherApplication(entity: TableEntityResult<Record<string, unknown>>, voucherType: string): VoucherApplication {
    let voucherApp: VoucherApplication = {
      // #region Base
      id: 0,
      allInfoCorrect: true,
      applicationStatus: this.voucherService.new,
      applicationType: voucherType,
      collectorID: 0,
      createNewWell: false,
      createDate: '',
      createdby: '',
      customData1: '',
      endDate: '',
      modifyDate: '',
      modifiedBy: '',
      newApplication: true,
      respondentID: 0,
      reviewDate: '',
      reviewedBy: '',
      startDate: '',
      statusComments: '',

      // #endregion
      // #region Form
      selectedWellId: 0,
      associatedWellAddress: '',
      associatedWellCity: '',
      associatedWellState: '',
      associatedWellZip: '',
      associatedWellCounty: '',
      applicantContactName: <string>entity.custFullName,
      applicantContactEmail: <string>entity.custEmail,
      applicantContactPhone: <string>entity.custPhone,

      //home address
      applicantAddress: <string>entity.custHomeAddr,
      applicantCity: <string>entity.custHomeCity,
      applicantState: <string>entity.custHomeState,
      applicantZip: <string>entity.custHomeZip,
      applicantCounty: <string>entity.custHomeCounty,
      //mailing address
      mailingAddress: <string>entity.custMailAddr,
      mailingCity: <string>entity.custCity,
      mailingState: <string>entity.custState,
      mailingZip: <string>entity.custZip,
      mailingCounty: '',

      communication: <string>entity.communicatingDifficulty,
      communicationComments: <string>entity.communicatingDifficulty,
      confidential: <string>entity.confidentiality,
      dontKnow: '',
      dressingBathing: <string>entity.dressingDifficulty,
      dressingBathingComments: '',
      englishProficiency: <string>entity.speakEnglish,
      focus: <string>entity.concentrateDifficulty,
      focusComment: '',
      hearing: <string>entity.hearingDifficulty,
      hearingComments: '',
      household1PersonIncome: '',
      householdPrivateWell: <string>entity.relyPrivateWell,
      householdSize: <string>entity.householdCount,
      householdSizeOther: <string>entity.householdCount,
      householdsServed: <string>entity.householdsServed,
      householdsServedOther: <string>entity.householdsServed,
      householdShared: <string>entity.shareWell,
      languagAtHome: <string>entity.homeLanguage,
      languageAtHomeOther: '',
      languageSpoken: <string>entity.communicateLanguage,
      languageWritten: <string>entity.writeLanguage,
      learning: <string>entity.learningDifficulty,
      learningComments: <string>entity.learningDifficulty,

      learnAboutVoucher: <string>entity.learnAboutVoucher,
      learnAboutVoucherOther: '',
      whoIsCompletingForm: <string>entity.whoIsCompletingForm,
      communityBasedOrganizationEmail: <string>entity.communityBasedOrganizationEmail,

      mobility: <string>entity.walkingDifficulty,
      mobilityComments: '',
      moods: <string>entity.moodDifficulty,
      moodsComments: '',
      needInterpreter: <string>entity.needInterpreter,
      needInterpreterType: <string>entity.interpreterType,
      needInterpreterTypeOther: '',
      preferredContactComments: '',
      preferredContactType: <string>entity.preferredContact,
      previousVoucher: '',
      primaryResidence: '',
      privateWellHoushold: '',
      privateWellProperty: '',
      propertyImpacted: '',
      propertyLocation: <string>entity.locatedLUBGM,
      raceEthDecline: false,
      raceEthOther: '',
      raceEthPrimary: <string>entity.raceEth2,
      raceEthPrimaryOther: '',
      residenceOther: '',
      selfSupport: <string>entity.errandsDifficulty,
      selfSupportComments: <string>entity.errandsDifficulty,
      tenant: <string>entity.householdsServed,
      vision: <string>entity.blind,
      visionComments: '',
      volunteerRaceEthInfo: entity.raceEthParticipate == 'Yes' ? 'true' : 'false',
      wellAddress: <string>entity.wellAddress,
      wellAddressCompany: '',
      wellAddressName: '',
      wellCity: <string>entity.wellCity,
      wellCounty: <string>entity.wellCounty,
      wellDamage: '',
      wellDamageComments: '',
      wellLabelTagNumber: <string>entity.wellLabelTag,
      wellLogNumber: <string>entity.wellLogNo,
      wellNitrateTest: <string>entity.testedLast12Months,
      wellNitrateTestOther: '',
      wellState: <string>entity.wellState,
      wellSystemPlasticRubber: <string>entity.wellSystemPlasticRubber,
      wellZip: <string>entity.wellZip,
      wildfire: [],
      wildfireOther: '',
      voucherResponseRowKey: entity.rowKey,

      // #endregion
      // #region REAL-D
      centralAmerican: false,
      mexican: false,
      southAmerican: false,
      otherLatinx: false,
      chamoru: false,
      marshallese: false,
      micronesian: false,
      nativeHawaiian: false,
      samoan: false,
      otherPacificIslander: false,
      slavic: false,
      westernEuropean: false,
      easternEuropean: false,
      otherWhite: false,
      americanIndian: false,
      alaskaNative: false,
      canadianFirstNation: false,
      indigenousCentralAmerican: false,
      africanAmerican: false,
      afroCarribbean: false,
      filipino: false,
      somali: false,
      ethiopian: false,
      otherAfrican: false,
      otherBlack: false,
      middleEastern: false,
      northAfrican: false,
      asianIndian: false,
      cambodian: false,
      chinese: false,
      myanmar: false,
      hmong: false,
      japanese: false,
      korean: false,
      laotian: false,
      southAsian: false,
      vietnamese: false,
      otherAsian: false,

      // #endregion
      selfGenerateVoucherIdentifier: false,
      selfGeneratedVoucherIdentifer: <string>entity.selfGeneratedVoucherIdentifer,
      wellLat: <string>entity.wellLatitude,
      wellLong: <string>entity.wellongitude,
      age15AndBelow: <number>entity.age15AndBelow,
      age16AndAbove: <number>entity.age16AndAbove,
      pregnancyInHousehold: false,
      infantsInHousehold: false,
      canvasCode: <string>entity.canvasCode,
      applicantAddressUnit: <string>entity.custMailUnit,
      createNewHousehold: false,
      selectedHouseholdId: 0,

      createNewHousingUnit: false,
      selectedHousingUnitId: 0,
      ohaWellId: <string>entity.wellId,
      ohaHousingUnitId: <string>entity.housingUnitId,
      ohaHouseholdId: <string>entity.householdId,
      householdLatitude: '',
      householdLongitude: '',
      scheduleTest: false,
      preferredSampleDays: <string>entity.preferredSampleDays,
      preferredSampleTimes: <string>entity.preferredSampleTimes,
      applicationSource: <string>entity.applicationSource,
      residenceType: <string>entity.rentType,
      residenceTypeOther: <string>entity.otherRentType,
    }

    //copy mailing address to home addres if home address is empty and mailing is not
    if((voucherApp.applicantAddress == null || voucherApp.applicantAddress.length == 0) &&
       voucherApp.mailingAddress != null && voucherApp.mailingAddress.length > 0) {
        voucherApp.applicantAddress = voucherApp.mailingAddress;
    }
    if((voucherApp.applicantCity == null || voucherApp.applicantCity.length == 0) &&
       voucherApp.mailingCity != null && voucherApp.mailingCity.length > 0) {
        voucherApp.applicantCity = voucherApp.mailingCity;
    }
    if((voucherApp.applicantState == null || voucherApp.applicantState.length == 0) &&
       voucherApp.mailingState != null && voucherApp.mailingState.length > 0) {
        voucherApp.applicantState = voucherApp.mailingState;
    }
    if((voucherApp.applicantZip == null || voucherApp.applicantZip.length == 0) &&
       voucherApp.mailingZip != null && voucherApp.mailingZip.length > 0) {
        voucherApp.applicantZip = voucherApp.mailingZip;
    }
    // Household  County
    if (voucherApp.applicantCounty != null && voucherApp.applicantCounty.length > 0) {
      voucherApp.applicantCounty = voucherApp.applicantCounty.replace("County", "").trim();
    }

    let pregnancy = <string>entity.pregnancyInHousehold;
    if(pregnancy != null && pregnancy.length > 0 && pregnancy.toLowerCase() == 'yes' ) {
      voucherApp.pregnancyInHousehold = true;
    }

    let infants = <string>entity.infantsInHousehold;
    if(infants != null && infants.length > 0 && infants.toLowerCase() == 'yes' ) {
      voucherApp.infantsInHousehold = true;
    }

    let scheduleTest = <string>entity.scheduleTest;
    if(scheduleTest != null && scheduleTest.length > 0 && scheduleTest.toLowerCase() == 'yes' ) {
      voucherApp.scheduleTest = true;
    }

    //self generated voucher
    if(voucherApp.selfGeneratedVoucherIdentifer != null && voucherApp.selfGeneratedVoucherIdentifer.length > 0) {
      voucherApp.selfGenerateVoucherIdentifier = true;

      let wellId = <string>entity.wellId;
      if(wellId != null && wellId.length > 0 && !isNaN(+wellId)) {
        voucherApp.selectedWellId = +wellId;
        voucherApp.createNewWell = false;
      }
      else {
        voucherApp.createNewWell = true;
      }
    }

    if(voucherApp.confidential != null && voucherApp.confidential == "no") {
      voucherApp.confidential = 'No';
    }
    else if(voucherApp.confidential != null && voucherApp.confidential == "yes") {
      voucherApp.confidential = 'Yes';
    }

    if(voucherApp.householdPrivateWell != null && voucherApp.householdPrivateWell == "private") {
      voucherApp.householdPrivateWell = this.voucherService.privateWellText;
    }
    if(voucherApp.householdPrivateWell != null && voucherApp.householdPrivateWell == "public") {
      voucherApp.householdPrivateWell = this.voucherService.publicWellText;
    }

    if(voucherApp.languageSpoken == 'Other') {
      voucherApp.languageSpoken = <string><string>entity.communicateLanguageOther;
    }

    voucherApp.householdShared = this.capitalizeFirstLetter(voucherApp.householdShared);

    // Rules
    this.setRealdRules(entity, voucherApp, voucherType);

    return voucherApp;
  }

 
  // Real-D Rules
  private setRealdRules(entity: TableEntityResult<Record<string, unknown>>, voucherApplication: VoucherApplication, voucherType: string) {
    if (voucherType == this.voucherService.wildfireVoucherType) {
      if (voucherApplication.languagAtHome == this.voucherService.EnglishOnly) {
        voucherApplication.languagAtHome = 'true';
      }
      else if (voucherApplication.languagAtHome == this.voucherService.other) {
        voucherApplication.languagAtHome = 'false';
      }
      else {
        voucherApplication.languagAtHome = '';
      }

      if (voucherApplication.privateWellHoushold == this.voucherService.Yes) {
        voucherApplication.privateWellHoushold = 'true';
      }
      else if (voucherApplication.languagAtHome == this.voucherService.No) {
        voucherApplication.privateWellHoushold = 'false';
      }
      else {
        voucherApplication.privateWellHoushold = '';
      }
    }

    if (voucherType == this.voucherService.lubgwmaTestingVoucherType) {
      if (voucherApplication.languagAtHome == this.voucherService.EnglishOnly) {
        voucherApplication.languagAtHome = 'true';
      }
      else {
        voucherApplication.languageAtHomeOther = voucherApplication.languagAtHome;
        voucherApplication.languagAtHome = 'false';
      }
    }

    let wfString = <string>entity.wildfire;
    if (wfString != null && wfString != undefined && wfString.length > 0) {
      voucherApplication.wildfire = JSON.parse(wfString);
    }

    // Real-D LUBGWMA
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType) {
      var tempEntity = <string>entity.raceEth;
      var tempEthnicities = tempEntity.split(',');
      var lubgwmaEthnicities = [];

      if (tempEthnicities != null && tempEthnicities.length > 0) {
        tempEthnicities.forEach(element => {
          element = element.replace('[', '').replace(']', '').replace('\"', '').replace('"', '').trim();
          lubgwmaEthnicities.push(element);
        });

        // #region Race/Ethnicity

        voucherApplication.africanAmerican = lubgwmaEthnicities.includes(this.voucherService.africanAmerican);
        voucherApplication.afroCarribbean = lubgwmaEthnicities.includes(this.voucherService.afroCarribbean);
        voucherApplication.alaskaNative = lubgwmaEthnicities.includes(this.voucherService.alaskaNative);
        voucherApplication.americanIndian = lubgwmaEthnicities.includes(this.voucherService.americanIndian);
        voucherApplication.asianIndian = lubgwmaEthnicities.includes(this.voucherService.asianIndian);
        voucherApplication.cambodian = lubgwmaEthnicities.includes(this.voucherService.cambodian);
        voucherApplication.canadianFirstNation = lubgwmaEthnicities.includes(this.voucherService.canadianFirstNation);
        voucherApplication.centralAmerican = lubgwmaEthnicities.includes(this.voucherService.centralAmerican);
        voucherApplication.chamoru = lubgwmaEthnicities.includes(this.voucherService.chamoru);
        voucherApplication.chinese = lubgwmaEthnicities.includes(this.voucherService.chinese);
        voucherApplication.easternEuropean = lubgwmaEthnicities.includes(this.voucherService.easternEuropean);
        voucherApplication.ethiopian = lubgwmaEthnicities.includes(this.voucherService.ethiopian);
        voucherApplication.filipino = lubgwmaEthnicities.includes(this.voucherService.filipino);
        voucherApplication.indigenousCentralAmerican = lubgwmaEthnicities.includes(this.voucherService.indigenousCentralAmerican);
        voucherApplication.hmong = lubgwmaEthnicities.includes(this.voucherService.hmong);
        voucherApplication.japanese = lubgwmaEthnicities.includes(this.voucherService.japanese);
        voucherApplication.korean = lubgwmaEthnicities.includes(this.voucherService.korean);
        voucherApplication.laotian = lubgwmaEthnicities.includes(this.voucherService.laotian);
        voucherApplication.marshallese = lubgwmaEthnicities.includes(this.voucherService.marshallese);
        voucherApplication.mexican = lubgwmaEthnicities.includes(this.voucherService.mexican);
        voucherApplication.micronesian = lubgwmaEthnicities.includes(this.voucherService.micronesian);
        voucherApplication.middleEastern = lubgwmaEthnicities.includes(this.voucherService.middleEastern);
        voucherApplication.myanmar = lubgwmaEthnicities.includes(this.voucherService.myanmar);
        voucherApplication.nativeHawaiian = lubgwmaEthnicities.includes(this.voucherService.nativeHawaiian);
        voucherApplication.northAfrican = lubgwmaEthnicities.includes(this.voucherService.northAfrican);
        voucherApplication.otherAsian = lubgwmaEthnicities.includes(this.voucherService.otherAsian);
        voucherApplication.otherAfrican = lubgwmaEthnicities.includes(this.voucherService.otherAfrican);
        voucherApplication.otherBlack = lubgwmaEthnicities.includes(this.voucherService.otherBlack);
        voucherApplication.otherLatinx = lubgwmaEthnicities.includes(this.voucherService.otherLatinx);
        voucherApplication.otherPacificIslander = lubgwmaEthnicities.includes(this.voucherService.otherPacificIslander);
        voucherApplication.otherWhite = lubgwmaEthnicities.includes(this.voucherService.otherWhite);
        voucherApplication.samoan = lubgwmaEthnicities.includes(this.voucherService.samoan);
        voucherApplication.slavic = lubgwmaEthnicities.includes(this.voucherService.slavic);
        voucherApplication.somali = lubgwmaEthnicities.includes(this.voucherService.somali);
        voucherApplication.southAmerican = lubgwmaEthnicities.includes(this.voucherService.southAmerican);
        voucherApplication.southAsian = lubgwmaEthnicities.includes(this.voucherService.southAsian);
        voucherApplication.vietnamese = lubgwmaEthnicities.includes(this.voucherService.vietnamese);
        voucherApplication.westernEuropean = lubgwmaEthnicities.includes(this.voucherService.westernEuropean);

        voucherApplication.dontKnow = lubgwmaEthnicities.includes(this.voucherService.dontKnow) ? 'Don\'t know' : '';
        voucherApplication.raceEthDecline = lubgwmaEthnicities.includes(this.voucherService.raceEthDecline);

        if (lubgwmaEthnicities.length > 0) {
          lubgwmaEthnicities.forEach(element => {
            if (element != this.voucherService.africanAmerican
              && element != this.voucherService.afroCarribbean && element != this.voucherService.alaskaNative
              && element != this.voucherService.americanIndian && element != this.voucherService.asianIndian
              && element != this.voucherService.cambodian && element != this.voucherService.canadianFirstNation
              && element != this.voucherService.centralAmerican && element != this.voucherService.chamoru
              && element != this.voucherService.chinese && element != this.voucherService.easternEuropean
              && element != this.voucherService.ethiopian && element != this.voucherService.filipino
              && element != this.voucherService.indigenousCentralAmerican && element != this.voucherService.hmong
              && element != this.voucherService.japanese && element != this.voucherService.korean
              && element != this.voucherService.laotian && element != this.voucherService.marshallese
              && element != this.voucherService.mexican && element != this.voucherService.micronesian
              && element != this.voucherService.middleEastern && element != this.voucherService.myanmar
              && element != this.voucherService.nativeHawaiian && element != this.voucherService.northAfrican
              && element != this.voucherService.otherAsian && element != this.voucherService.otherAfrican
              && element != this.voucherService.otherBlack && element != this.voucherService.otherLatinx
              && element != this.voucherService.micronesian && element != this.voucherService.otherPacificIslander
              && element != this.voucherService.otherWhite && element != this.voucherService.samoan
              && element != this.voucherService.slavic && element != this.voucherService.somali
              && element != this.voucherService.southAmerican && element != this.voucherService.southAsian
              && element != this.voucherService.vietnamese && element != this.voucherService.westernEuropean) {

              voucherApplication.raceEthOther = element;
            }

          });
        }

        // #endregion
      }
    }

    // Real-D Wildfire
    if (voucherType == this.voucherService.wildfireVoucherType) {
      var ethnicities = <string[]>entity.ancestryIdentity;

      if (ethnicities != null && ethnicities.length > 0) {
        voucherApplication.africanAmerican = ethnicities.includes(this.voucherService.africanAmerican);
        voucherApplication.afroCarribbean = ethnicities.includes(this.voucherService.afroCarribbean);
        voucherApplication.alaskaNative = ethnicities.includes(this.voucherService.alaskaNative);
        voucherApplication.americanIndian = ethnicities.includes(this.voucherService.americanIndian);
        voucherApplication.asianIndian = ethnicities.includes(this.voucherService.asianIndian);
        voucherApplication.cambodian = ethnicities.includes(this.voucherService.cambodian);
        voucherApplication.canadianFirstNation = ethnicities.includes(this.voucherService.canadianFirstNation);
        voucherApplication.centralAmerican = ethnicities.includes(this.voucherService.centralAmerican);
        voucherApplication.chamoru = ethnicities.includes(this.voucherService.chamoru);
        voucherApplication.chinese = ethnicities.includes(this.voucherService.chinese);
        voucherApplication.easternEuropean = ethnicities.includes(this.voucherService.easternEuropean);
        voucherApplication.ethiopian = ethnicities.includes(this.voucherService.ethiopian);
        voucherApplication.filipino = ethnicities.includes(this.voucherService.filipino);
        voucherApplication.indigenousCentralAmerican = ethnicities.includes(this.voucherService.indigenousCentralAmerican);
        voucherApplication.hmong = ethnicities.includes(this.voucherService.hmong);
        voucherApplication.japanese = ethnicities.includes(this.voucherService.japanese);
        voucherApplication.korean = ethnicities.includes(this.voucherService.korean);
        voucherApplication.laotian = ethnicities.includes(this.voucherService.laotian);
        voucherApplication.marshallese = ethnicities.includes(this.voucherService.marshallese);
        voucherApplication.mexican = ethnicities.includes(this.voucherService.mexican);
        voucherApplication.micronesian = ethnicities.includes(this.voucherService.micronesian);
        voucherApplication.middleEastern = ethnicities.includes(this.voucherService.middleEastern);
        voucherApplication.myanmar = ethnicities.includes(this.voucherService.myanmar);
        voucherApplication.nativeHawaiian = ethnicities.includes(this.voucherService.nativeHawaiian);
        voucherApplication.northAfrican = ethnicities.includes(this.voucherService.northAfrican);
        voucherApplication.otherAsian = ethnicities.includes(this.voucherService.otherAsian);
        voucherApplication.otherAfrican = ethnicities.includes(this.voucherService.otherAfrican);
        voucherApplication.otherBlack = ethnicities.includes(this.voucherService.otherBlack);
        voucherApplication.otherLatinx = ethnicities.includes(this.voucherService.otherLatinx);
        voucherApplication.otherPacificIslander = ethnicities.includes(this.voucherService.otherPacificIslander);
        voucherApplication.otherWhite = ethnicities.includes(this.voucherService.otherWhite);
        voucherApplication.samoan = ethnicities.includes(this.voucherService.samoan);
        voucherApplication.slavic = ethnicities.includes(this.voucherService.slavic);
        voucherApplication.somali = ethnicities.includes(this.voucherService.somali);
        voucherApplication.southAmerican = ethnicities.includes(this.voucherService.southAmerican);
        voucherApplication.southAsian = ethnicities.includes(this.voucherService.southAmerican);
        voucherApplication.vietnamese = ethnicities.includes(this.voucherService.vietnamese);
        voucherApplication.westernEuropean = ethnicities.includes(this.voucherService.westernEuropean);
      }
    }

    if (voucherApplication.applicantContactName == null || voucherApplication.applicantContactName.length == 0) {
      //assume waiting list if no applicant contact name
      voucherApplication.applicantContactName = <string>entity.waitListName;
      voucherApplication.applicantContactPhone = <string>entity.waitListPhone;
      voucherApplication.applicantContactEmail = <string>entity.waitListEmail;
      voucherApplication.applicantAddress = <string>entity.waitListAddress;
      voucherApplication.applicantCity = <string>entity.waitListCity;
      voucherApplication.applicantState = <string>entity.waitListState;
      voucherApplication.applicantZip = <string>entity.waitListZip;
    }

    // Well Damage
    if (voucherApplication.wellDamage != null && voucherApplication.wellDamage.length > 0 && !this.voucherService.wellDamageOptions.includes(voucherApplication.wellDamage)) {
      voucherApplication.wellDamageComments = voucherApplication.wellDamage;
      voucherApplication.wellDamage = this.voucherService.other;
    }

    // Primary Residence
    if (voucherApplication.primaryResidence != null && voucherApplication.primaryResidence.length > 0 && !this.voucherService.primaryResidenceOptions.includes(voucherApplication.primaryResidence)) {
      voucherApplication.residenceOther = voucherApplication.primaryResidence;
      voucherApplication.primaryResidence = this.voucherService.other;
    }

    // Private households
    if (voucherApplication.privateWellHoushold == undefined || voucherApplication.privateWellHoushold == null) {
      voucherApplication.privateWellHoushold = '';
    }
    else if (voucherApplication.privateWellHoushold == this.voucherService.Yes) {
      voucherApplication.privateWellHoushold = 'True';
    }
    else if (voucherApplication.privateWellHoushold == this.voucherService.No) {
      voucherApplication.privateWellHoushold = 'False';
    }
    else {
      voucherApplication.privateWellHoushold = '';
    }

    // House Holds served Other
    if (voucherApplication.householdsServed != null && voucherApplication.householdsServed.length > 0 && !this.voucherService.householdsServedOptions.includes(voucherApplication.householdsServed)) {
      voucherApplication.householdsServedOther = voucherApplication.householdsServed;
      voucherApplication.householdsServed = this.voucherService.other;
    }

    // Wildfires Other
    if (voucherApplication.wildfire != null && voucherApplication.wildfire.length > 0) {
      var otherWildfires = voucherApplication.wildfire.filter(w => !this.voucherService.wildfireList.includes(w));
      if (otherWildfires != null && otherWildfires.length > 0) {
        var wildfireList = voucherApplication.wildfire.filter(w => this.voucherService.wildfireList.includes(w));
        wildfireList.push(this.voucherService.other);
        voucherApplication.wildfire = wildfireList;
        voucherApplication.wildfireOther = otherWildfires.join(",");
      }
    }

    // Well County
    if (voucherApplication.wellCounty != null && voucherApplication.wellCounty.length > 0) {
      voucherApplication.wellCounty = voucherApplication.wellCounty.replace("County", "").trim();
    }

    // Dressing Bathing Other
    if (voucherApplication.dressingBathing != null && voucherApplication.dressingBathing.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.dressingBathing)) {
      voucherApplication.dressingBathingComments = voucherApplication.dressingBathing;
      voucherApplication.dressingBathing = this.voucherService.other;
    }


    // Preferred Contact Type
    if(voucherApplication.preferredContactType != null && voucherApplication.preferredContactType.length > 0 ) {
      //capitalize the first character
      voucherApplication.preferredContactType = this.capitalizeFirstLetter(voucherApplication.preferredContactType);
    }
    if (voucherApplication.preferredContactType != null && voucherApplication.preferredContactType.length > 0 && !this.voucherService.preferredContactTypeOptions.includes(voucherApplication.preferredContactType)) {
      voucherApplication.preferredContactComments = voucherApplication.preferredContactType;
      voucherApplication.preferredContactType = this.voucherService.other;
    }

    // Learning Other
    if (voucherApplication.learning != null && voucherApplication.learning.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.learning)) {
      voucherApplication.learningComments = voucherApplication.learning;
      voucherApplication.learning = this.voucherService.other;
    }

    // Self Support Other
    if (voucherApplication.selfSupport != null && voucherApplication.selfSupport.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.selfSupport)) {
      voucherApplication.selfSupportComments = voucherApplication.selfSupport;
      voucherApplication.selfSupport = this.voucherService.other;
    }

    // Household Income
    if (voucherType == this.voucherService.wildfireVoucherType) {
      if (voucherApplication.householdSize.includes("1")) {
        voucherApplication.household1PersonIncome = <string>entity.income1;
      }
      else if (voucherApplication.householdSize.includes("2")) {
        voucherApplication.household1PersonIncome = <string>entity.income2;
      }
      else if (voucherApplication.householdSize.includes("3")) {
        voucherApplication.household1PersonIncome = <string>entity.income3;
      }
      else if (voucherApplication.householdSize.includes("4")) {
        voucherApplication.household1PersonIncome = <string>entity.income4;
      }
      else if (voucherApplication.householdSize.includes("5")) {
        voucherApplication.household1PersonIncome = <string>entity.income5;
      }
      else if (voucherApplication.householdSize.includes("6")) {
        voucherApplication.household1PersonIncome = <string>entity.income6;
      }
      else {
        voucherApplication.household1PersonIncome = this.voucherService.preferNotToanswer;
      }
    }

    // People in Household
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.householdSize != null && voucherApplication.householdSize.length > 0) {

      if (voucherApplication.householdSize != '1 person' && voucherApplication.householdSize != '2 people'
        && voucherApplication.householdSize != '3 people' && voucherApplication.householdSize != '4 people'
        && voucherApplication.householdSize != '5 people' && voucherApplication.householdSize != '6 people'
        && voucherApplication.householdSize != '7 people' && voucherApplication.householdSize != '8 people'
        && voucherApplication.householdSize != '9 people' && voucherApplication.householdSize != '10 people') {
        voucherApplication.householdSizeOther = voucherApplication.householdSize;
        voucherApplication.householdSize = 'Other';
      }
    }

    // Confidential
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.confidential != null && voucherApplication.confidential.length > 0) {
      let temp = voucherApplication.confidential.substring(2);

      if (temp.startsWith('Ye') || temp.startsWith('ye')) {
        voucherApplication.confidential = 'Yes'
      }

      if ( temp.startsWith('No')) {
        voucherApplication.confidential = 'No'
      }
    }

    // Household Private Well
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.householdPrivateWell != null && voucherApplication.householdPrivateWell.length > 0) {
      if (voucherApplication.householdPrivateWell.startsWith('Yes') || voucherApplication.householdPrivateWell.startsWith('private') ) {
        voucherApplication.householdPrivateWell = 'Yes, the well is private. I own or rent the property and the well'
      }

      if (voucherApplication.householdPrivateWell.startsWith('No') || voucherApplication.householdPrivateWell.startsWith('public')) {
        voucherApplication.householdPrivateWell = 'No, the well is part of a public water system. I pay a water bill'
      }
    }

    // Property Location
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.propertyLocation != null && voucherApplication.propertyLocation.length > 0) {
      if (voucherApplication.propertyLocation.startsWith('Yes')) {
        voucherApplication.propertyLocation = 'Yes'
      }

      if (voucherApplication.propertyLocation.startsWith('No')) {
        voucherApplication.propertyLocation = 'No'
      }

      if (voucherApplication.propertyLocation.startsWith('Unsure')) {
        voucherApplication.propertyLocation = 'Unsure'
      }
    }

    // Well Nitrate Test
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.wellNitrateTest != null && voucherApplication.wellNitrateTest.length > 0) {

      if (voucherApplication.wellNitrateTest != 'Yes, Morrow County Health Department tested my well for nitrate.'
        && voucherApplication.wellNitrateTest != 'Yes, Umatilla County Public Health tested my well for nitrate.'
        && voucherApplication.wellNitrateTest != 'No, the Counties did not test my well for nitrate.'
        && voucherApplication.wellNitrateTest != 'Unsure') {
        voucherApplication.wellNitrateTestOther = voucherApplication.wellNitrateTest;
        voucherApplication.wellNitrateTest = 'Other';
      }
    }

    // Moods
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.moods != null && voucherApplication.moods.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.moods)) {
      voucherApplication.moodsComments = voucherApplication.moods;
      voucherApplication.moods = this.voucherService.other;
    }

    // Communication
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.communication != null && voucherApplication.communication.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.communication)) {
      voucherApplication.communicationComments = voucherApplication.communication;
      voucherApplication.communication = this.voucherService.other;
    }

    // Focus
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.focus != null && voucherApplication.focus.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.focus)) {
      voucherApplication.focusComment = voucherApplication.focus;
      voucherApplication.focus = this.voucherService.other;
    }

    // Mobility
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.mobility != null && voucherApplication.mobility.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.mobility)) {
      voucherApplication.mobilityComments = voucherApplication.mobility;
      voucherApplication.mobility = this.voucherService.other;
    }

    // Vision
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.vision != null && voucherApplication.vision.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.vision)) {
      voucherApplication.visionComments = voucherApplication.vision;
      voucherApplication.vision = this.voucherService.other;
    }

    // Hearing
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.hearing != null && voucherApplication.hearing.length > 0 && !this.voucherService.genericOptions.includes(voucherApplication.hearing)) {
      voucherApplication.hearingComments = voucherApplication.hearing;
      voucherApplication.hearing = this.voucherService.other;
    }

    // Interperter
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.needInterpreterType != null && voucherApplication.needInterpreterType.length > 0) {

      if (voucherApplication.needInterpreterType != 'Spoken language interpreter'
        && voucherApplication.needInterpreterType != 'American Sign Language interpreter'
        && voucherApplication.needInterpreterType != 'Deaf interpreter for Deaf/Blind, additional barriers, or both'
        && voucherApplication.needInterpreterType != 'Contact sign language (PSE) interpreter') {
        voucherApplication.needInterpreterTypeOther = voucherApplication.needInterpreterType;
        voucherApplication.needInterpreterType = 'Other';
      }
    }

    // Well Address
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType && voucherApplication.wellAddress == '') {
      voucherApplication.wellAddress = voucherApplication.applicantAddress;
      voucherApplication.wellCity = voucherApplication.applicantCity;
      voucherApplication.wellState = voucherApplication.applicantState;
      voucherApplication.wellZip = voucherApplication.applicantZip;
      voucherApplication.wellCounty = voucherApplication.applicantCounty;
    }

    // Learn About Voucher
    if (voucherType == this.voucherService.lubgwmaTestingVoucherType
      && voucherApplication.learnAboutVoucher != null && voucherApplication.learnAboutVoucher.length > 0) {

      if (voucherApplication.learnAboutVoucher != this.voucherService.ohaWebsite
        && voucherApplication.learnAboutVoucher != this.voucherService.otherWebsite
        && voucherApplication.learnAboutVoucher != this.voucherService.ohaPressRelease
        && voucherApplication.learnAboutVoucher != this.voucherService.communityEvent
        && voucherApplication.learnAboutVoucher != this.voucherService.facebook
        && voucherApplication.learnAboutVoucher != this.voucherService.twitter
        && voucherApplication.learnAboutVoucher != this.voucherService.email
        && voucherApplication.learnAboutVoucher != this.voucherService.radio
        && voucherApplication.learnAboutVoucher != this.voucherService.newspaper
        && voucherApplication.learnAboutVoucher != this.voucherService.countyPublicHealth
        && voucherApplication.learnAboutVoucher != this.voucherService.church
        && voucherApplication.learnAboutVoucher != this.voucherService.workplace
        && voucherApplication.learnAboutVoucher != this.voucherService.tribalHealth
        && voucherApplication.learnAboutVoucher != this.voucherService.groceryStore
        && voucherApplication.learnAboutVoucher != this.voucherService.searchEngine) {
        voucherApplication.learnAboutVoucherOther = voucherApplication.learnAboutVoucher;
        voucherApplication.learnAboutVoucher = this.voucherService.other;
      }
    }  
  }

  // Entity Processed
  async setEntityProcessed(voucherType: string, rowKey: string): Promise<any> {
    try {
      const client = this.getTableClient(voucherType);

      if(voucherType == this.voucherService.lubgwmaTestingVoucherType || voucherType == this.voucherService.lubgwmatestingSurvey123VoucherType) {
        voucherType = this.voucherService.lubgwmaType;
      }

      await client.deleteEntity(voucherType, rowKey);
    }
    catch (e) {
      console.log(e);
      this.toastr.error(e);
      this.toastr.error("Unable to save Voucher Import: " + rowKey + ".  Contact OIS");
    }

    return;
  }

  capitalizeFirstLetter(str: string): string {
    if(str == null || str.length == 0)  {
      return str
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}